import React, { useEffect, useState, Children } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { trackCalendlyConversion } from './GoogleTagLoader';
export function HeroSection() {
  const phrases = ['Personalized Outreach', 'Zero No-Shows', 'Higher Attendance', 'Always Running'];
  const [currentPhraseIndex, setCurrentPhraseIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(true);
  useEffect(() => {
    const intervalId = setInterval(() => {
      setIsVisible(false);
      setTimeout(() => {
        setCurrentPhraseIndex(prevIndex => (prevIndex + 1) % phrases.length);
        setIsVisible(true);
      }, 300);
    }, 2200);
    return () => clearInterval(intervalId);
  }, []);
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        delayChildren: 0.3,
        staggerChildren: 0.2
      }
    }
  };
  const itemVariants = {
    hidden: {
      y: 20,
      opacity: 0
    },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5
      }
    }
  };
  return <section className="relative bg-gradient-to-b from-indigo-50 to-white pt-32 pb-20 md:pt-40 md:pb-32 overflow-hidden" data-id="element-221">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-222">
        <motion.div className="lg:grid lg:grid-cols-12 lg:gap-16 items-center" initial="hidden" animate="visible" variants={containerVariants} data-id="element-223">
          {/* Left Content */}
          <motion.div className="lg:col-span-6 flex flex-col justify-center text-center lg:text-left" variants={itemVariants} data-id="element-224">
            <h1 className="text-4xl tracking-tight font-extrabold text-gray-900 sm:text-5xl md:text-6xl mb-6" data-id="element-225">
              <span className="block" data-id="element-226">Fill Every Seat.</span>
              <span className={`block text-indigo-600 transition-opacity duration-500 whitespace-nowrap ${isVisible ? 'opacity-100' : 'opacity-0'}`} style={{
              minHeight: '1.25em'
            }} data-id="element-227">
                {phrases[currentPhraseIndex]}
              </span>
              <span className="block" data-id="element-228">with Turnout</span>
            </h1>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl max-w-2xl mx-auto lg:mx-0" data-id="element-229">
              Running meetups, community events, or dev conferences? Turnout
              sends personalized outreach to every registrant so more people
              actually show up. Fewer no-shows, less manual work, better events.
            </p>
            <div className="mt-8 sm:mt-10 flex flex-col sm:flex-row gap-4 justify-center lg:justify-start" data-id="element-230">
              <a href="https://calendly.com/dan-at-turnout/turnout-general-30-min-call" target="_blank" rel="noopener noreferrer" onClick={trackCalendlyConversion} className="inline-flex items-center justify-center px-8 py-4 border border-transparent text-lg font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 transition-colors duration-300" data-id="element-231">
                See How It Works
                <ArrowRight className="ml-2 h-5 w-5" data-id="element-232" />
              </a>
              <a href="#features" className="inline-flex items-center justify-center px-8 py-4 border border-gray-300 text-lg font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors duration-300" data-id="element-233">
                Explore Features
              </a>
            </div>
          </motion.div>

          {/* Right Visual - Integration Cloud */}
          <motion.div className="mt-16 lg:mt-0 lg:col-span-6 relative" variants={itemVariants} data-id="element-234">
            <div className="relative h-[420px] w-full flex items-center justify-center" data-id="element-235">
              {/* Central Hub - Turnout Logo */}
              <div className="absolute z-20 bg-white rounded-2xl shadow-xl p-6 border border-indigo-100 flex flex-col items-center" data-id="element-236">
                <img src="https://cdn.magicpatterns.com/uploads/mGJzFe33DXdGGbrUZqM2ty/app-logo.png" alt="Turnout" className="h-16 w-16 object-contain mb-2" data-id="element-237" />
                <span className="font-bold text-gray-900" data-id="element-238">Turnout</span>
              </div>

              {/* Orbiting Integrations */}
              <div className="absolute inset-0" data-id="element-239">
                {/* Top Left - Gmail */}
                <div className="absolute top-2 left-8 md:left-14 bg-white p-3 rounded-xl shadow-lg border border-gray-100 transform -rotate-12 hover:scale-110 transition-transform" data-id="element-240">
                  <img src="https://cdn.magicpatterns.com/uploads/i3m9Dz8tVyFX3XYH5aSycT/gmail-icon.svg" alt="Gmail" className="h-8 w-8 object-contain" data-id="element-241" />
                </div>
                {/* Top Center - WhatsApp */}
                <div className="absolute top-0 left-1/2 -translate-x-1/2 bg-white p-3 rounded-xl shadow-lg border border-gray-100 transform rotate-3 hover:scale-110 transition-transform" data-id="element-242">
                  <img src="https://cdn.magicpatterns.com/uploads/j1eFkudG3snN4BnwPPvfbX/whatsapp-icon.svg" alt="WhatsApp" className="h-8 w-8 object-contain" data-id="element-243" />
                </div>
                {/* Top Right - Outlook */}
                <div className="absolute top-6 right-8 md:right-14 bg-white p-3 rounded-xl shadow-lg border border-gray-100 transform rotate-12 hover:scale-110 transition-transform" data-id="element-244">
                  <img src="https://cdn.magicpatterns.com/uploads/wciZK1t5un9NpvyYG4xtuo/outlook-icon.svg" alt="Outlook" className="h-8 w-8 object-contain" data-id="element-245" />
                </div>
                {/* Right Upper - Canva */}
                <div className="absolute top-1/3 right-0 bg-white p-3 rounded-xl shadow-lg border border-gray-100 transform rotate-6 hover:scale-110 transition-transform" data-id="element-246">
                  <img src="https://cdn.magicpatterns.com/uploads/iKQEnA2bTqFX8YLk3u7yRT/canva-logo.png" alt="Canva" className="h-8 w-8 object-contain" data-id="element-247" />
                </div>
                {/* Right Lower - Google Drive */}
                <div className="absolute bottom-1/3 right-2 bg-white p-3 rounded-xl shadow-lg border border-gray-100 transform -rotate-6 hover:scale-110 transition-transform" data-id="element-248">
                  <img src="https://cdn.magicpatterns.com/uploads/2yqcSmzQqZi9cdSsmgsLpN/google-drive-icon.svg" alt="Google Drive" className="h-8 w-8 object-contain" data-id="element-249" />
                </div>
                {/* Bottom Right - Airtable */}
                <div className="absolute bottom-6 right-14 bg-white p-3 rounded-xl shadow-lg border border-gray-100 transform rotate-6 hover:scale-110 transition-transform" data-id="element-250">
                  <img src="https://cdn.magicpatterns.com/uploads/bQ3BrNNoSGHPhJ2LPvGch1/airtable-icon.svg" alt="Airtable" className="h-8 w-8 object-contain" data-id="element-251" />
                </div>
                {/* Bottom Center - Notion */}
                <div className="absolute bottom-0 left-1/2 -translate-x-1/2 bg-white p-3 rounded-xl shadow-lg border border-gray-100 transform -rotate-3 hover:scale-110 transition-transform" data-id="element-252">
                  <img src="https://cdn.magicpatterns.com/uploads/1kGheTjtnQKFkEbYZYttia/notion-icon.svg" alt="Notion" className="h-8 w-8 object-contain" data-id="element-253" />
                </div>
                {/* Bottom Left - Tripleseat */}
                <div className="absolute bottom-8 left-10 bg-white p-3 rounded-xl shadow-lg border border-gray-100 transform rotate-6 hover:scale-110 transition-transform" data-id="element-254">
                  <img src="https://cdn.magicpatterns.com/uploads/pBvjpazwySPVaj2JeBgC2A/tripleseat-logo.png" alt="Tripleseat" className="h-8 w-8 object-contain" data-id="element-255" />
                </div>
                {/* Left Upper - iMessage */}
                <div className="absolute top-1/3 left-0 bg-white p-3 rounded-xl shadow-lg border border-gray-100 transform -rotate-12 hover:scale-110 transition-transform" data-id="element-256">
                  <img src="https://cdn.magicpatterns.com/uploads/b7Bk8etBDenG3R7oDnec67/imessage-icon.svg" alt="iMessage" className="h-8 w-8 object-contain" data-id="element-257" />
                </div>
                {/* Left Lower - OneDrive */}
                <div className="absolute bottom-1/3 left-2 bg-white p-3 rounded-xl shadow-lg border border-gray-100 transform rotate-3 hover:scale-110 transition-transform" data-id="element-258">
                  <img src="https://cdn.magicpatterns.com/uploads/uQgGV18a3e7tTLcoaM3Puu/onedrive-icon.svg" alt="OneDrive" className="h-8 w-8 object-contain" data-id="element-259" />
                </div>
              </div>

              {/* Connecting Lines (Decorative SVG) */}
              <svg className="absolute inset-0 w-full h-full z-10 pointer-events-none opacity-20" viewBox="0 0 400 400" data-id="element-260">
                <circle cx="200" cy="200" r="100" fill="none" stroke="currentColor" strokeWidth="1" strokeDasharray="4 4" className="text-indigo-300" data-id="element-261" />
                <circle cx="200" cy="200" r="160" fill="none" stroke="currentColor" strokeWidth="1" strokeDasharray="4 4" className="text-indigo-200" data-id="element-262" />
              </svg>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>;
}